import React, { useState } from 'react'
import httpService from '../../services/httpService'
import DefaultPanelContainer from '../common/defaultPanelContainer'
import BaseForm from '../common/baseForm'
import Joi from 'joi-browser'
import { Button, Alert } from 'reactstrap'

class DebugMatchingForm extends BaseForm {
  state = {
    data: { 
      userId: '', 
      parsedHouseId: '', 
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    },
    errors: {}
  }

  componentDidMount() {
    if (this.props.initialData) {
      this.setState(prev => ({
        data: { ...prev.data, ...this.props.initialData }
      }))
    }
  }

  handleChange = ({ currentTarget: input }) => {
    const data = { ...this.state.data }
    data[input.name] = input.value
    this.setState({ data })
    
    // Update URL parameters when userId or parsedHouseId change
    if ((input.name === 'userId' || input.name === 'parsedHouseId') && this.props.onUrlParamsChange) {
      this.props.onUrlParamsChange(input.name, input.value)
    }
  }

  schema = {
    userId: Joi.string().guid({ version: 'uuidv4' }).required().label('User ID'),
    parsedHouseId: Joi.string().guid({ version: 'uuidv4' }).required().label('Parsed House ID'),
    startDate: Joi.date().required().label('Start Date'),
    endDate: Joi.date().required().label('End Date')
  }

  doSubmit = async () => {
    const { userId, parsedHouseId, startDate, endDate } = this.state.data
    this.props.onSubmit({
      userId,
      parsedHouseId,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString()
    })
  }

  render() {
    return (
      <DefaultPanelContainer title="Debug Matching Pipeline">
        <div className="mb-3">
          <Alert color="info">
            <strong>Debug Mode:</strong> This tool runs the same matching pipeline as production but without creating any database records or sending notifications. 
            It's safe to use against production data to investigate matching issues.
          </Alert>
        </div>
        <form onSubmit={this.handleSubmit}>
          {this.renderInput('userId', 'User ID', 'text')}
          {this.renderInput('parsedHouseId', 'Parsed House ID', 'text')}
          {this.renderInput('startDate', 'Start Date', 'date')}
          {this.renderInput('endDate', 'End Date', 'date')}
          {this.renderButton('Run Debug Analysis', 'primary', this.props.isLoading)}
        </form>
      </DefaultPanelContainer>
    )
  }
}

function DebugResults({ result }) {
  const [showDetails, setShowDetails] = useState(false)
  const [showRawOutput, setShowRawOutput] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)

  if (!result) return null

  const copyRawToClipboard = async () => {
    const text = result.formattedOutput || ''
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setCopyError(false)
      } else {
        // Fallback for older browsers - improved implementation
        const success = await fallbackCopyToClipboard(text)
        if (success) {
          setCopied(true)
          setCopyError(false)
        } else {
          setCopyError(true)
        }
      }
      if (copied) {
        setTimeout(() => setCopied(false), 1500)
      }
    } catch (e) {
      console.warn('Failed to copy to clipboard:', e)
      setCopyError(true)
      setTimeout(() => setCopyError(false), 3000)
    }
  }

  const fallbackCopyToClipboard = (text) => {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      ta.style.top = '-9999px'
      ta.setAttribute('readonly', '')
      document.body.appendChild(ta)
      
      const selection = document.getSelection()
      const selected = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
      
      ta.select()
      ta.setSelectionRange(0, 99999) // For mobile devices
      
      const successful = document.execCommand('copy')
      document.body.removeChild(ta)
      
      // Restore previous selection
      if (selected) {
        selection.removeAllRanges()
        selection.addRange(selected)
      }
      
      return successful
    } catch (err) {
      console.warn('Fallback copy failed:', err)
      return false
    }
  }

  return (
    <div>
      <DefaultPanelContainer title="Debug Results">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div className="small text-muted">
            <strong>Summary:</strong>
            <span className="ms-2">Matches: {result.matchCount}</span>
            <span className="ms-3">Steps: {result.debugSteps.length}</span>
          </div>
          <div>
            <Button 
              color={copyError ? "outline-danger" : (copied ? "outline-success" : "outline-secondary")} 
              size="sm" 
              className="me-2" 
              onClick={copyRawToClipboard}
            >
              {copyError ? 'Copy Failed!' : (copied ? 'Copied!' : 'Copy Raw Output')}
            </Button>
          </div>
        </div>
        <div className="mb-3">
          <div className="row">
            <div className="col-md-3">
              <strong>Status:</strong> 
              <span className={`ms-2 badge ${result.success ? 'bg-success' : 'bg-danger'}`}>
                {result.success ? 'Success' : 'Failed'}
              </span>
            </div>
            <div className="col-md-3">
              <strong>Execution Time:</strong> {result.executionTimeSeconds.toFixed(2)}s
            </div>
            <div className="col-md-3">
              <strong>Final Matches:</strong> {result.matchCount}
            </div>
            <div className="col-md-3">
              <strong>Debug Steps:</strong> {result.debugSteps.length}
            </div>
          </div>
        </div>

        {result.errorMessage && (
          <Alert color="danger">
            <strong>Error:</strong> {result.errorMessage}
          </Alert>
        )}

        <div className="mb-3">
          <Button 
            color="secondary" 
            size="sm" 
            className="me-2"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Show'} Step Details
          </Button>
          <Button 
            color="secondary" 
            size="sm"
            onClick={() => setShowRawOutput(!showRawOutput)}
          >
            {showRawOutput ? 'Hide' : 'Show'} Raw Output
          </Button>
        </div>

        {showDetails && (
          <DefaultPanelContainer title="Pipeline Steps" className="mt-3">
            {result.debugSteps.map((step, index) => (
              <div key={index} className="border-bottom pb-3 mb-3">
                <div className="row">
                  <div className="col-md-8">
                    <h6>{step.taskName}</h6>
                    <p className="mb-1"><strong>Action:</strong> {step.action}</p>
                    <p className="mb-1"><strong>Matches:</strong> {step.matchCount}</p>
                  </div>
                  <div className="col-md-4 text-end">
                    <small className="text-muted">{new Date(step.timestamp).toLocaleTimeString()}</small>
                  </div>
                </div>
                {Object.keys(step.details).length > 0 && (
                  <div className="mt-2">
                    <strong>Details:</strong>
                    <ul className="list-unstyled ms-3">
                      {Object.entries(step.details).map(([key, value]) => (
                        <li key={key}>
                          <code>{key}:</code> {JSON.stringify(value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </DefaultPanelContainer>
        )}

        {showRawOutput && (
          <DefaultPanelContainer title="Raw Debug Output" className="mt-3">
            <pre className="bg-light p-3" style={{ fontSize: '12px', overflow: 'auto' }}>
              {result.formattedOutput}
            </pre>
          </DefaultPanelContainer>
        )}
      </DefaultPanelContainer>
    </div>
  )
}

export default function DebugMatching(props) {
  const [result, setResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const queryParams = new URLSearchParams(props.location.search)
  const prefilledUserId = queryParams.get('userId')
  const prefilledParsedHouseId = queryParams.get('parsedHouseId')

  const updateUrlParams = (paramName, value) => {
    const currentParams = new URLSearchParams(window.location.search)
    
    if (value && value.trim()) {
      currentParams.set(paramName, value.trim())
    } else {
      currentParams.delete(paramName)
    }
    
    const newUrl = `${window.location.pathname}${currentParams.toString() ? '?' + currentParams.toString() : ''}`
    window.history.replaceState({}, '', newUrl)
  }

  const handleSubmit = async (formData) => {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await httpService.post('diagnostics/debug-matching', formData)
      setResult(response.data)
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while running the debug analysis')
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewAnalysis = () => {
    setResult(null)
    setError(null)
    window.location.href = '/debug-matching'
  }

  // Pre-fill form if query parameters are present
  const formProps = {
    onSubmit: handleSubmit,
    isLoading,
    onUrlParamsChange: updateUrlParams,
    initialData: prefilledUserId || prefilledParsedHouseId ? {
      userId: prefilledUserId || '',
      parsedHouseId: prefilledParsedHouseId || '',
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    } : undefined
  }

  return (
    <>
      {result && (
        <div className="mb-3">
          <Button color="secondary" onClick={handleNewAnalysis}>
            New Analysis
          </Button>
        </div>
      )}

      {!result && (
        <>
          <DebugMatchingForm {...formProps} />
          {isLoading && (
            <div className="text-center mt-3 d-flex align-items-center justify-content-center">
              <div className="spinner-border text-primary me-2" role="status" aria-hidden="true"></div>
              Running debug analysis...
            </div>
          )}
        </>
      )}

      {error && (
        <Alert color="danger" className="mt-3">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {result && <DebugResults result={result} />}
    </>
  )
}
